/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, createContext, useContext, useEffect, useState, ReactNode } from "react";
import { WithChildren } from "../../helpers";

export interface PageLink {
    title: string;
    hide?: boolean;
    path: string;
    isActive?: boolean;
    isSeparator?: boolean;
}

export type PageButtonLink = PageLink & {
    cssClass?: string;
    cssExtraClass?: string;
    onClick?: (..._params: any) => void;
    renderComponent?: React.FunctionComponent<any>;
    renderComponentProps?: any;
};

export interface PageDataContextModel {
    pageTitle?: string;
    setPageTitle: (_title: string) => void;
    pageDescription?: string;
    setPageDescription: (_description: string) => void;
    pageBreadcrumbs?: Array<PageLink>;
    setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
    pageToolbarLinks?: Array<PageLink>;
    setPageToolbarLinks: (_toolbarLinks: Array<PageLink>) => void;
    pageToolbarButtons?: Array<PageButtonLink>;
    setPageToolbarButtons: (_toolbarButtons: Array<PageButtonLink>) => void;
    leftPageCustomComponent?: JSX.Element | null;
    setLeftPageCustomComponent?: (_customComponent: JSX.Element | null) => void;
    rightPageCustomComponent?: JSX.Element | null;
    setRightPageCustomComponent?: (_customComponent: JSX.Element | null) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
    setPageTitle: (_title: string) => {},
    setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
    setPageDescription: (_description: string) => {},
    setPageToolbarLinks: (_toolbarLinks: Array<PageButtonLink>) => {},
    setPageToolbarButtons: (_toolbarButtons: Array<PageButtonLink>) => {},
    setLeftPageCustomComponent: (_customComponent: JSX.Element | null) => {},
    setRightPageCustomComponent: (_customComponent: JSX.Element | null) => {},
});

const PageDataProvider: FC<WithChildren> = ({ children }) => {
    const [pageTitle, setPageTitle] = useState<string>("");
    const [pageDescription, setPageDescription] = useState<string>("");
    const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
    const [pageToolbarLinks, setPageToolbarLinks] = useState<Array<PageLink>>([]);
    const [pageToolbarButtons, setPageToolbarButtons] = useState<Array<PageButtonLink>>([]);
    const [leftPageCustomComponent, setLeftPageCustomComponent] = useState<JSX.Element | null>(
        null
    );
    const [rightPageCustomComponent, setRightPageCustomComponent] = useState<JSX.Element | null>(
        null
    );
    const value: PageDataContextModel = {
        pageTitle,
        setPageTitle,
        pageDescription,
        setPageDescription,
        pageBreadcrumbs,
        setPageBreadcrumbs,
        pageToolbarButtons,
        setPageToolbarButtons,
        pageToolbarLinks,
        setPageToolbarLinks,
        leftPageCustomComponent,
        setLeftPageCustomComponent,
        rightPageCustomComponent,
        setRightPageCustomComponent,
    };
    return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>;
};

function usePageData() {
    return useContext(PageDataContext);
}

type Props = {
    description?: string;
    breadcrumbs?: Array<PageLink>;
    pageToolbarButtons?: Array<PageButtonLink>;
    leftPageCustomComponent?: JSX.Element;
    rightPageCustomComponent?: JSX.Element;
};

const PageTitle: FC<Props & WithChildren> = ({
    children,
    description,
    breadcrumbs,
    pageToolbarButtons,
    leftPageCustomComponent,
    rightPageCustomComponent,
}) => {
    const {
        setPageTitle,
        setPageDescription,
        setPageBreadcrumbs,
        setPageToolbarButtons,
        pageToolbarLinks,
        setPageToolbarLinks,
        setLeftPageCustomComponent,
        setRightPageCustomComponent,
    } = usePageData();

    useEffect(() => {
        if (children) {
            setPageTitle(children.toString());
        }
        return () => {
            setPageTitle("");
        };
    }, [children]);

    useEffect(() => {
        if (description) {
            setPageDescription(description);
        }
        return () => {
            setPageDescription("");
        };
    }, [description]);

    useEffect(() => {
        if (breadcrumbs) {
            setPageBreadcrumbs(breadcrumbs);
        }
        return () => {
            setPageBreadcrumbs([]);
        };
    }, [breadcrumbs]);

    useEffect(() => {
        if (pageToolbarButtons) {
            setPageToolbarButtons(pageToolbarButtons);
        }
        return () => {
            setPageToolbarButtons([]);
        };
    }, [pageToolbarButtons]);

    useEffect(() => {
        if (pageToolbarLinks) {
            setPageToolbarLinks(pageToolbarLinks);
        }
        return () => {
            setPageToolbarLinks([]);
        };
    }, [pageToolbarLinks]);

    useEffect(() => {
        if (leftPageCustomComponent) {
            setLeftPageCustomComponent && setLeftPageCustomComponent(leftPageCustomComponent);
        }
        return () => {
            setLeftPageCustomComponent && setLeftPageCustomComponent(null);
        };
    }, [leftPageCustomComponent]);

    useEffect(() => {
        if (rightPageCustomComponent) {
            setRightPageCustomComponent && setRightPageCustomComponent(rightPageCustomComponent);
        }
        return () => {
            setRightPageCustomComponent && setRightPageCustomComponent(null);
        };
    }, [rightPageCustomComponent]);

    return <></>;
};

const PageDescription: FC<WithChildren> = ({ children }) => {
    const { setPageDescription } = usePageData();
    useEffect(() => {
        if (children) {
            setPageDescription(children.toString());
        }
        return () => {
            setPageDescription("");
        };
    }, [children]);
    return <></>;
};

export { PageDescription, PageTitle, PageDataProvider, usePageData };
